import BookIcon from 'components/icons/Book';
import FileTextIcon from 'components/icons/FileText';
import Space from 'components/Space';
import React, { FC, useContext, useMemo } from 'react';
import useSWR from 'swr';
import MenteeNotifications from 'types/notifications/MenteeNotifications';
import MentorNotifications from 'types/notifications/MentorNotifications';
import User from 'types/User';
import LinkDropDown from './components/LinkDropDown';
import NavigationLink from './components/NavigationLink';

type ContextType = {
  user: User | undefined;
};
const SectionLinksContext = React.createContext({} as ContextType);
const { Provider } = SectionLinksContext;

const SectionLinks: FC<{ isCollapsed?: boolean }> = ({ isCollapsed }) => {
  const { data: user }: { data?: User } = useSWR('/auth/profile');
  const value = useMemo(
    () => ({
      user,
    }),
    [user],
  );

  return (
    <Provider value={value}>
      {user?.UserRole?.some((userRole) => userRole.role === 'MENTEE') && (
        <MenteeLinks isCollapsed={isCollapsed}/>
      )}
      {user?.UserRole?.some((userRole) => userRole.role === 'MENTOR') && (
        <MentorLinks isCollapsed={isCollapsed}/>
      )}
      {user?.UserRole?.some((userRole) => userRole.role === 'ADMIN') && (
        <AdminLinks isCollapsed={isCollapsed}/>
      )}
      <Space height={3} />
    </Provider>
  );
};

const MenteeLinks: FC<{ isCollapsed?: boolean }> = ({ isCollapsed }) => {
  const { user } = useContext(SectionLinksContext);
  const { data: menteeNotifications } = useSWR<MenteeNotifications>(
    '/user/notification/mentee',
  );
  return (
    <>
      <LinkDropDown
        icon={<BookIcon />}
        title={
          user?.UserRole && user.UserRole.length > 1
            ? 'Mentorat (mentoré)'
            : 'Mentorat'
        }
        href="/mentee/mentorship"
        isCollapsed={isCollapsed}
      >
        {(user?.menteeStatus === 'ELIGIBLE' ||
          user?.menteeStatus === 'MENTORSHIP_ACTIVE') && (
            <NavigationLink
              title="Votre mentorat"
              href="/mentee/mentorship/management/"
              isCollapsed={isCollapsed}
              activeLink="/mentee/mentorship/management"
              notification={!!menteeNotifications?.interviewNotification}
            />
          )}
        {user?.MenteeMentorship &&
          user.MenteeMentorship[0] &&
          user.MenteeMentorship[0].status !== 'MENTOR_ACCEPTING' &&
          user.menteeStatus === 'MENTORSHIP_ACTIVE' && (
            <NavigationLink
              title="Messagerie"
              href="/mentee/mentorship/messaging"
              isCollapsed={isCollapsed}
              notification={!!menteeNotifications?.messagingNotification}
            />
          )}
        <NavigationLink
          title="Questionnaire de mentorat"
          href="/mentee/mentorship/edit-form"
          isCollapsed={isCollapsed}
        />
        {user?.menteeStatus !== 'FILLING_FORM' && (
          <NavigationLink
            title="Charte" 
            href="/mentee/mentorship/sign-chart"
            isCollapsed={isCollapsed}
        />
        )}
        <NavigationLink
          title="Supports pédagogiques"
          href="/mentee/mentorship/documents"
          isCollapsed={isCollapsed}
        />
      </LinkDropDown>
    </>
  );
};

const MentorLinks: FC<{ isCollapsed?: boolean }> = ({ isCollapsed }) => {
  const { user } = useContext(SectionLinksContext);
  const { data: mentorNotifications } = useSWR<MentorNotifications>(
    '/user/notification/mentor',
  );
  return (
    <>
      <LinkDropDown
        icon={<BookIcon />}
        title={
          user?.UserRole && user.UserRole.length > 1
            ? 'Mentorat (mentor)'
            : 'Mentorat'
        }
        href="/mentor/mentorship"
        isCollapsed={isCollapsed}
      >
        {(user?.mentorStatus === 'ACCEPTED' || user?.mentorStatus === 'AVAILABLE' ||
          user?.mentorStatus === 'MENTORSHIP_ACTIVE') && (
            <NavigationLink
              title="Vos mentorats"
              href="/mentor/mentorship/management/mentees/"
              isCollapsed={isCollapsed}
              activeLink="/mentor/mentorship/management"
              notification={
                mentorNotifications?.mentorshipNotification ||
                mentorNotifications?.interviewNotification
              }
            />
          )}
        {(user?.mentorStatus === 'ACCEPTED' || user?.mentorStatus === 'AVAILABLE' ||
          user?.mentorStatus === 'MENTORSHIP_ACTIVE') && (
            <NavigationLink
              title="Messagerie"
              href="/mentor/mentorship/messaging"
              isCollapsed={isCollapsed}
              notification={!!mentorNotifications?.messagingNotification}
            />
          )}
        <NavigationLink
          title="Questionnaire de mentorat"
          href="/mentor/mentorship/edit-form"
          isCollapsed={isCollapsed}
        />
        {user?.mentorStatus !== 'FILLING_FORM' && (
          <NavigationLink 
            title="Charte" 
            href="/mentor/mentorship/sign-chart" 
            isCollapsed={isCollapsed}
            />
        )}
        {user?.mentorStatus !== 'FILLING_FORM' &&
          user?.mentorStatus !== 'SIGNING_CHART' && (
            <NavigationLink
              title="Formation mentor"
              href="/mentor/mentorship/confirm-training"
              isCollapsed={isCollapsed}
            />
          )}
        <NavigationLink
          title="Supports pédagogiques"
          href="/mentor/mentorship/documents"
          isCollapsed={isCollapsed}
        />
      </LinkDropDown>
    </>
  );
};

const AdminLinks: FC<{ isCollapsed?: boolean }> = ({ isCollapsed }) => {
  const { user } = useContext(SectionLinksContext);
  return (
    <>
      <LinkDropDown
        icon={<BookIcon />}
        title={
          user?.UserRole && user.UserRole.length > 1
            ? 'Mentorat (admin)'
            : 'Mentorat'
        }
        href="/admin/mentoring"
        isCollapsed={isCollapsed}
      >
        <NavigationLink 
          title="Mentorés" 
          href="/admin/mentoring/mentees" 
          isCollapsed={isCollapsed}
          />
        <NavigationLink 
          title="Mentors" 
          href="/admin/mentoring/mentors"
          isCollapsed={isCollapsed}
          />
        <NavigationLink
          title="Binômes"
          href="/admin/mentoring/binomes"
          isCollapsed={isCollapsed}
        />
        <NavigationLink
          title="Chargés de mentorat"
          href="/admin/mentoring/referees"
          isCollapsed={isCollapsed}
        />
      </LinkDropDown>
      <NavigationLink
        icon={<FileTextIcon />}
        title="Fichiers"
        href="/admin/files"
        isCollapsed={isCollapsed}
      ></NavigationLink>
    </>
  );
};
export default SectionLinks;
